/**
 * Applying a restored version snapshot to client state (HG-3A G3-02b).
 *
 * Migration 249 restores the database correctly: in restore mode the dimensions
 * group, the override record and the per-competency development level are
 * written LITERALLY from the snapshot, so a snapshot predating migration 248
 * restores them to null instead of leaving today's values attached to an older
 * grade.
 *
 * The client has to do the same thing, and before this module it did not. The
 * restore handler spread the current row and overwrote only score, analysis,
 * goals and evidence, so after restoring a pre-248 version the editor went on
 * showing the CURRENT dimensions beside the RESTORED grade until the next
 * reload. The database was right and the screen was wrong, which is the harder
 * version of the bug to notice.
 *
 * The rule, stated once here and tested in
 * __tests__/assessment-restore.test.ts:
 *
 *   - the ten captured fields come from the snapshot, and a key the snapshot
 *     does not carry becomes null, never the current value;
 *   - framework_id and framework_version fall back to the current row, because
 *     they are NOT NULL since migration 234 and a snapshot predating it has
 *     nothing to offer;
 *   - historical is never touched by a restore: it records how a row came into
 *     existence, not what it contains (ICD 9.3).
 */

/** The fields migration 249 restores literally. Order is documentation only. */
export const RESTORED_CAPTURE_FIELDS = [
  'how_many',
  'how_often',
  'tem_outcome',
  'limiting_factor',
  'proposed_grade',
  'override_flag',
  'override_category',
  'override_note',
  'development_level',
  'development_level_source',
] as const;

export type RestoredCaptureField = (typeof RESTORED_CAPTURE_FIELDS)[number];

/**
 * Read one field out of a per-competency snapshot. Absent or JSON null both
 * mean "this version did not have a value", which restores as null.
 */
function fromSnapshot(snapshot: Record<string, unknown>, key: string): unknown {
  const value = snapshot[key];
  return value === undefined ? null : value;
}

/**
 * Merge one competency's snapshot into its current row, matching migration
 * 249's restore semantics exactly.
 *
 * `current` is the row as the editor holds it; `snapshot` is that competency's
 * entry from the session composite `{pilot_role: {competency: {...}}}`.
 */
export function applyRestoredCompetencySnapshot<T extends Record<string, unknown>>(
  current: T,
  snapshot: Record<string, unknown>,
): T {
  const restored: Record<string, unknown> = { ...current };

  for (const field of RESTORED_CAPTURE_FIELDS) {
    restored[field] = fromSnapshot(snapshot, field);
  }

  // NOT NULL since migration 234: a snapshot without them keeps the current
  // stamp rather than restoring a null the database would refuse.
  restored.framework_id = snapshot.framework_id ?? current.framework_id ?? null;
  restored.framework_version = snapshot.framework_version ?? current.framework_version ?? null;

  // performance_criteria follows the captured fields: it is materialised at
  // save time and belongs to the version it was materialised in.
  restored.performance_criteria = fromSnapshot(snapshot, 'performance_criteria');

  // Deliberately absent: historical. No restore may move a record's
  // provenance flag.

  return restored as T;
}
